/**
 * Confirm dialogs and task-page modals for /schedule/tasks.
 * Route: /schedule/tasks.
 * Does not own schedule loading, the chart engine, or toolbar state.
 */
import { Fragment } from 'react';
import { AssignUsersDrawer } from '../components/assignments/AssignUsersDrawer';
import { MOCK_ASSIGN_USERS, MOCK_ASSIGN_LOCATIONS, MOCK_ASSIGN_TASKS } from '../components/assignments/assignUsersMockData';
import { USE_FLOATING_LOCATION_PANEL, ENABLE_TARGETS_QUANTITY_PANEL } from '../ganttPageFlags';
import { LocationHierarchyFloatingPanel } from '../../../location-hierarchy-floating';
import { LocationHierarchyModal } from '../../../location-hierarchy';
import { PredecessorTaskPickerPanel, savePredecessorGridShell } from '../components/predecessor-picker';
import { AddTaskModal } from '../components/AddTaskModal';
import { DeleteTaskConfirmModal } from '../components/DeleteTaskConfirmModal';
import { SetProjectBaselineModal } from '../components/SetProjectBaselineModal';
import { Modal, Text, Group, Button, SimpleGrid, Box, Stack } from '@mantine/core';
import { IconDeviceFloppy, IconMapPin, IconTags } from '@tabler/icons-react';
import { GANTT_LEAVE_CONFIRM_Z_INDEX, GanttLeaveConfirmModal } from '../components/GanttLeaveConfirmModal';
import { GanttLiveScheduleOffModal } from '../components/GanttLiveScheduleOffModal';
import { GanttKeyboardShortcutsModal } from '../components/GanttKeyboardShortcutsModal';

export function GanttPageDialogs(p: Record<string, any>) {
  const {
    assignDrawerOpen,
    setAssignDrawerOpen,
    assignDrawerMode,
    projectId,
    assignPresentation,
    assignTargetsLocked,
    assignSelectionTargets,
    assignSelectionTaskIds,
    handleAssignSelectionTaskIdsChange,
    handleAssignSelectionLocationIdRemove,
    handleAssignSelectionLocationIdsAdd,
    handleAssignSelectionClearScope,
    assignInitialScope,
    assignInitialTaskTargets,
    assignUserOptions,
    assignLocationOptions,
    assignTaskSearchCatalog,
    assignFilterLeafTasks,
    mockAssignments,
    liveAssignments,
    setMockAssignments,
    setLiveAssignments,
    refreshLiveAssignments,
    assignUsersLoading,
    assignAssignmentsLoading,
    locationModalOpen,
    closeLocationInlineEditor,
    setLocationModalOpen,
    setLocationModalTaskId,
    locationModalTaskId,
    currentTaskLocationId,
    currentTaskLocationName,
    enabledLocationIds,
    assignLocationToTask,
    requestReassignLocations,
    reassignLocationsLoading,
    predecessorPickerOpen,
    closePredecessorPicker,
    predecessorPickerTaskId,
    store,
    predecessorPickerCurrentInput,
    handlePredecessorPickerApplyInput,
    resolvePredecessorWbsCode,
    predecessorPickerMode,
    predecessorGridShell,
    setPredecessorGridShell,
    addTaskModalOpen,
    addTaskParentId,
    addTaskSubmitting,
    addTaskError,
    closeAddTaskModal,
    handleCreateTask,
    deleteTaskTarget,
    deleteTaskSubmitting,
    setDeleteTaskTarget,
    handleConfirmDeleteTask,
    baselineModalOpen,
    baselineModalStep,
    projectDisplayName,
    baselineSubmitting,
    saving,
    baselineModalBlockedMessage,
    closeBaselineModal,
    handleBaselineSaveAndContinue,
    handleConfirmCreateBaseline,
    saveConfirmOpen,
    setSaveConfirmOpen,
    sessionMetadata,
    handleConfirmSaveToBackend,
    reassignConfirmOpen,
    setReassignConfirmOpen,
    modalContainer,
    totalSessionChanges,
    confirmReassignLocations,
    bulkUnknownTypeConfirmOpen,
    bulkUnknownTypeLoading,
    setBulkUnknownTypeConfirmOpen,
    setBulkUnknownTypePreview,
    bulkUnknownTypePreview,
    confirmBulkUnknownTaskTypes,
    discardConfirmOpen,
    setDiscardConfirmOpen,
    discarding,
    handleDiscardChanges,
    targetsDiscardPending,
    handleTargetsDiscardCancel,
    handleTargetsDiscardConfirm,
    liveScheduleOffModalOpen,
    setLiveScheduleOffModalOpen,
    keyboardShortcutsOpen,
    setKeyboardShortcutsOpen,
    leaveBlocker,
    onConfirmLeaveRef,
  } = p;

  return (
    <Fragment>
  <AssignUsersDrawer
    opened={assignDrawerOpen}
    onClose={() => setAssignDrawerOpen(false)}
    projectId={assignDrawerMode === 'mock' ? projectId || 'mock-project' : projectId}
    mode={assignDrawerMode}
    presentation={assignPresentation}
    targetsLocked={assignTargetsLocked}
    selectionSync={!assignTargetsLocked}
    selectionTargets={assignSelectionTargets}
    selectionTaskIds={assignSelectionTaskIds}
    onSelectionTaskIdsChange={handleAssignSelectionTaskIdsChange}
    onSelectionLocationIdRemove={handleAssignSelectionLocationIdRemove}
    onSelectionLocationIdsAdd={handleAssignSelectionLocationIdsAdd}
    onSelectionClearScope={handleAssignSelectionClearScope}
    initialScope={assignInitialScope}
    initialTaskTargets={assignInitialTaskTargets}
    userOptions={assignDrawerMode === 'mock' ? MOCK_ASSIGN_USERS : assignUserOptions}
    locationOptions={
      assignDrawerMode === 'mock' ? MOCK_ASSIGN_LOCATIONS : assignLocationOptions
    }
    taskCatalog={assignDrawerMode === 'mock' ? MOCK_ASSIGN_TASKS : assignTaskSearchCatalog}
    filterLeafTasks={
      assignDrawerMode === 'mock'
        ? MOCK_ASSIGN_TASKS.filter((t: any) => t.locationId).map((t: any) => ({
            kind: 'task' as const,
            id: t.id,
            label: t.label,
            locationId: t.locationId as string,
            breadcrumb: t.breadcrumb,
          }))
        : assignFilterLeafTasks
    }
    existingAssignments={
      assignDrawerMode === 'mock' ? mockAssignments : liveAssignments
    }
    onExistingAssignmentsChange={
      assignDrawerMode === 'mock' ? setMockAssignments : setLiveAssignments
    }
    onAssignmentsMutated={
      assignDrawerMode === 'live' ? () => refreshLiveAssignments() : undefined
    }
    usersLoading={assignDrawerMode === 'live' ? assignUsersLoading : false}
    assignmentsLoading={
      assignDrawerMode === 'live' ? assignAssignmentsLoading : false
    }
  />

  {USE_FLOATING_LOCATION_PANEL ? (
    <LocationHierarchyFloatingPanel
      opened={locationModalOpen}
      onClose={() => {
        closeLocationInlineEditor();
        setLocationModalOpen(false);
        setLocationModalTaskId(null);
      }}
      projectId={projectId}
      taskId={locationModalTaskId}
      selectedLocationId={currentTaskLocationId ?? undefined}
      selectedLocationName={currentTaskLocationName}
      enabledLocationIds={enabledLocationIds}
      onSelectLocation={assignLocationToTask}
      onReassignLocations={requestReassignLocations}
      reassignLocationsLoading={reassignLocationsLoading}
      title="Location Hierarchy"
    />
  ) : (
    <LocationHierarchyModal
      opened={locationModalOpen}
      onClose={() => {
        closeLocationInlineEditor();
        setLocationModalOpen(false);
        setLocationModalTaskId(null);
      }}
      projectId={projectId}
      taskId={locationModalTaskId}
      selectedLocationId={currentTaskLocationId ?? undefined}
      selectedLocationName={currentTaskLocationName}
      enabledLocationIds={enabledLocationIds}
      onSelectLocation={assignLocationToTask}
      onReassignLocations={requestReassignLocations}
      reassignLocationsLoading={reassignLocationsLoading}
      title="Location Hierarchy"
    />
  )}

  <PredecessorTaskPickerPanel
    opened={predecessorPickerOpen}
    onClose={closePredecessorPicker}
    targetTaskId={predecessorPickerTaskId}
    tasks={store.getTasks()}
    links={store.getLinks()}
    currentInput={predecessorPickerCurrentInput}
    onApplyInput={handlePredecessorPickerApplyInput}
    resolveWbsCode={resolvePredecessorWbsCode}
    title="Pick predecessors"
    presentation={
      predecessorPickerMode === 'panel'
        ? 'modal'
        : predecessorGridShell === 'floating'
          ? 'floating'
          : 'drawer'
    }
    onGridShellChange={
      predecessorPickerMode === 'grid'
        ? (shell: 'drawer' | 'floating') => {
            setPredecessorGridShell(shell);
            savePredecessorGridShell(shell);
          }
        : undefined
    }
  />

  <AddTaskModal
    opened={addTaskModalOpen}
    parentTask={addTaskParentId ? store.getTask(addTaskParentId) ?? null : null}
    tasks={store.getTasks()}
    submitting={addTaskSubmitting}
    submitError={addTaskError}
    resolvePredecessorWbsCode={resolvePredecessorWbsCode}
    onClose={() => {
      if (!addTaskSubmitting) {
        closeAddTaskModal();
      }
    }}
    onSubmit={handleCreateTask}
  />

  <DeleteTaskConfirmModal
    task={deleteTaskTarget}
    submitting={deleteTaskSubmitting}
    onClose={() => {
      if (!deleteTaskSubmitting) setDeleteTaskTarget(null);
    }}
    onConfirm={handleConfirmDeleteTask}
  />

  <SetProjectBaselineModal
    opened={baselineModalOpen}
    step={baselineModalStep}
    projectDisplayName={projectDisplayName}
    submitting={baselineSubmitting || (baselineModalStep === 'save-gate' && saving)}
    activeRevisionBlockedMessage={baselineModalBlockedMessage}
    onClose={closeBaselineModal}
    onSaveAndContinue={handleBaselineSaveAndContinue}
    onConfirmCreate={handleConfirmCreateBaseline}
  />

  <Modal
    opened={saveConfirmOpen}
    onClose={() => {
      if (!saving) setSaveConfirmOpen(false);
    }}
    title="Save changes to server?"
    centered
  >
    <Text size="sm" mb="sm">
      This will upload your unsaved schedule edits to the server.
    </Text>
    <Text size="sm" mb="md" fw={600}>
      Changed: {sessionMetadata.taskCount}{' '}
      {sessionMetadata.taskCount === 1 ? 'task' : 'tasks'}, {sessionMetadata.linkCount}{' '}
      {sessionMetadata.linkCount === 1 ? 'link' : 'links'}
    </Text>
    <Group justify="flex-end" gap="sm">
      <Button variant="default" onClick={() => setSaveConfirmOpen(false)} disabled={saving}>
        Cancel
      </Button>
      <Button
        color="brand"
        leftSection={<IconDeviceFloppy size={14} />}
        loading={saving}
        onClick={() => void handleConfirmSaveToBackend()}
      >
        Save to Server
      </Button>
    </Group>
  </Modal>

  <Modal
    opened={reassignConfirmOpen}
    onClose={() => {
      if (!saving && !reassignLocationsLoading) setReassignConfirmOpen(false);
    }}
    title="Reassign locations?"
    centered
    closeOnClickOutside={!saving && !reassignLocationsLoading}
    closeOnEscape={!saving && !reassignLocationsLoading}
    zIndex={GANTT_LEAVE_CONFIRM_Z_INDEX}
    portalProps={modalContainer ? { target: modalContainer } : undefined}
  >
    {totalSessionChanges > 0 ? (
      <>
        <Text size="sm" mb="sm">
          You have unsaved schedule changes. Reassign updates locations on the server and
          reloads the schedule, which would overwrite local edits. Save to Server first, then
          reassign.
        </Text>
        <Text size="sm" mb="md" fw={600}>
          Unsaved: {sessionMetadata.taskCount}{' '}
          {sessionMetadata.taskCount === 1 ? 'task' : 'tasks'}, {sessionMetadata.linkCount}{' '}
          {sessionMetadata.linkCount === 1 ? 'link' : 'links'}
        </Text>
      </>
    ) : (
      <Text size="sm" mb="md">
        Remap tasks to locations using the saved location hierarchy for this project, then
        reload the schedule from the server.
      </Text>
    )}
    <Group justify="flex-end" gap="sm">
      <Button
        variant="default"
        onClick={() => setReassignConfirmOpen(false)}
        disabled={saving || reassignLocationsLoading}
      >
        Cancel
      </Button>
      <Button
        leftSection={<IconMapPin size={14} />}
        loading={saving || reassignLocationsLoading}
        onClick={() => void confirmReassignLocations()}
      >
        {totalSessionChanges > 0 ? 'Save and reassign' : 'Reassign'}
      </Button>
    </Group>
  </Modal>

  <Modal
    opened={bulkUnknownTypeConfirmOpen}
    onClose={() => {
      if (!bulkUnknownTypeLoading) {
        setBulkUnknownTypeConfirmOpen(false);
        setBulkUnknownTypePreview(null);
      }
    }}
    title="Fix unknown task types?"
    centered
    closeOnClickOutside={!bulkUnknownTypeLoading}
    closeOnEscape={!bulkUnknownTypeLoading}
    zIndex={GANTT_LEAVE_CONFIRM_Z_INDEX}
    portalProps={modalContainer ? { target: modalContainer } : undefined}
  >
    <Text size="sm" mb="sm">
      Unknown leaf tasks become Construction Activity. Unknown summary tasks become Location
      when a location id is already available (own or inherited). Summaries without a location
      are skipped.
    </Text>
    {bulkUnknownTypePreview ? (
      <SimpleGrid cols={2} spacing="xs" mb="md">
        {(
          [
            {
              label: 'Unknown found',
              value: bulkUnknownTypePreview.unknownCount,
              hint: 'Tasks with empty or unknown type',
            },
            {
              label: 'Construction activity',
              value: bulkUnknownTypePreview.constructionActivityCount,
              hint: 'Unknown leaf tasks to update',
            },
            {
              label: 'Location',
              value: bulkUnknownTypePreview.locationCount,
              hint: 'Unknown summaries with a location id',
            },
            {
              label: 'Skipped',
              value: bulkUnknownTypePreview.skippedSummaryNoLocationCount,
              hint: 'Summaries with no resolvable location',
            },
          ] as const
        ).map((stat) => (
          <Box
            key={stat.label}
            p="sm"
            style={{
              borderRadius: 8,
              border: '1px solid var(--mantine-color-default-border)',
              background: 'var(--mantine-color-default)',
            }}
          >
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: 0.3 }}>
                {stat.label}
              </Text>
              <Text size="xl" fw={700} lh={1.2}>
                {stat.value}
              </Text>
              <Text size="xs" c="dimmed" lh={1.35}>
                {stat.hint}
              </Text>
            </Stack>
          </Box>
        ))}
      </SimpleGrid>
    ) : null}
    <Group justify="flex-end" gap="sm">
      <Button
        variant="default"
        onClick={() => {
          setBulkUnknownTypeConfirmOpen(false);
          setBulkUnknownTypePreview(null);
        }}
        disabled={bulkUnknownTypeLoading}
      >
        Cancel
      </Button>
      <Button
        leftSection={<IconTags size={14} />}
        loading={bulkUnknownTypeLoading}
        onClick={() => void confirmBulkUnknownTaskTypes()}
      >
        Update types
      </Button>
    </Group>
  </Modal>

  <Modal
    opened={discardConfirmOpen}
    onClose={() => setDiscardConfirmOpen(false)}
    title="Discard unsaved changes?"
    centered
  >
    <Text size="sm" mb="md">
      This will reload the schedule from the server and remove all changes you have not saved
      with Save to Server. This cannot be undone.
    </Text>
    <Group justify="flex-end" gap="sm">
      <Button variant="default" onClick={() => setDiscardConfirmOpen(false)}>
        Cancel
      </Button>
      <Button color="red" loading={discarding} onClick={handleDiscardChanges}>
        Discard changes
      </Button>
    </Group>
  </Modal>

  <Modal
    opened={ENABLE_TARGETS_QUANTITY_PANEL && targetsDiscardPending != null}
    onClose={handleTargetsDiscardCancel}
    title="Discard unsaved look-ahead edits?"
    centered
    closeOnClickOutside={false}
  >
    <Text size="sm" mb="md">
      {targetsDiscardPending?.kind === 'zoom'
        ? 'You have unsaved look-ahead edits. Changing Scale will discard them.'
        : targetsDiscardPending?.kind === 'view'
          ? 'You have unsaved look-ahead edits. Switching view will discard them.'
          : 'This will discard all unsaved look-ahead edits. This cannot be undone.'}
    </Text>
    <Group justify="flex-end" gap="sm">
      <Button variant="default" onClick={handleTargetsDiscardCancel}>
        Cancel
      </Button>
      <Button color="red" onClick={handleTargetsDiscardConfirm}>
        {targetsDiscardPending?.kind === 'drafts'
          ? 'Discard changes'
          : 'Discard and continue'}
      </Button>
    </Group>
  </Modal>

  <GanttLiveScheduleOffModal
    opened={liveScheduleOffModalOpen}
    onClose={() => setLiveScheduleOffModalOpen(false)}
  />

  <GanttKeyboardShortcutsModal
    opened={keyboardShortcutsOpen}
    onClose={() => setKeyboardShortcutsOpen(false)}
  />

  <GanttLeaveConfirmModal
    blocker={leaveBlocker}
    onConfirmLeave={async () => {
      await onConfirmLeaveRef.current();
    }}
  />
    </Fragment>
  );
}
